import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, Users, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { fetchAvailableServicemen } from '../../api/projectMember';

export interface UserSearchItem {
  id: string | number;
  serviceman_id: string | number;
  name: string;
  email?: string;
  phone?: string;
  user_type?: string;
  avatar?: string;
  is_already_member?: boolean;
}

interface MarketplaceSearchProps {
  projectId: string;
  onSelectMulti?: (users: UserSearchItem[]) => void;
  onSelect?: (user: UserSearchItem) => void;
}

export const MarketplaceSearch: React.FC<MarketplaceSearchProps> = ({ projectId, onSelectMulti, onSelect }) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserSearchItem[]>([]);

  // Debounce search input (400ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch data using TanStack React Query
  const { data: craftmenList = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['availableServicemen', projectId, debouncedQuery],
    queryFn: async () => {
      const res = await fetchAvailableServicemen(projectId, debouncedQuery.trim() || undefined);
      const rawList = res?.content || res?.data || res || [];
      const list = Array.isArray(rawList) ? rawList : [];

      return list.map((item: any): UserSearchItem => ({
        id: item.id,
        serviceman_id: item.serviceman_id,
        name: item.name,
        email: item.email || '',
        phone: item.phone || '',
        user_type: item.user_type || 'provider-serviceman',
        avatar: item.avatar,
        is_already_member: item.is_already_member || false,
      }));
    },
    staleTime: 1000 * 60 * 2,
  });

  const toggleSelectUser = (user: UserSearchItem) => {
    if (user.is_already_member) return;
    
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      const updated = exists ? prev.filter((u) => u.id !== user.id) : [...prev, user];
      if (onSelectMulti) onSelectMulti(updated);
      if (onSelect && updated.length > 0) onSelect(updated[updated.length - 1]);
      return updated;
    });
  };

  const getRoleLabel = (userType?: string) => {
    if (userType === 'provider-architect') {
      return { label: 'Arsitek', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
    }
    return { label: 'Teknisi', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
  };

  return (
    <div className="space-y-5">
      <div className="relative">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
          {isLoading ? <Loader2 size={16} className="animate-spin text-blue-600" /> : <Search size={16} />}
        </div>
        <input
          type="text"
          placeholder="Cari nama, email, atau nomor telepon di tim pelaksana Anda..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full pl-10 pr-4 py-3 text-sm bg-slate-50 border border-slate-200/80 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 placeholder:text-slate-400 font-medium"
        />
        {searchInput !== debouncedQuery && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 font-medium">
            Mengetik...
          </span>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between text-xs text-slate-500 font-medium px-1">
          <span>Hasil Pencarian ({craftmenList.length} Ditemukan):</span>
          <span className="font-bold text-slate-700">{selectedUsers.length} Tercentang</span>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-100 rounded-3xl animate-pulse" />
            ))}
          </div>
        )}

        {isError && (
          <div className="py-8 text-center text-slate-400 text-xs font-medium border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50">
            Gagal memuat data tim pelaksana. <button onClick={() => refetch()} className="text-blue-600 underline font-semibold">Coba lagi</button>
          </div>
        )}

        {!isLoading && !isError && craftmenList.length === 0 && (
          <div className="py-12 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
            <Users size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-bold text-slate-700">Tidak Ada Tim Ditemukan</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
              {debouncedQuery 
                ? `Tidak ada hasil untuk pencarian "${debouncedQuery}"` 
                : 'Belum ada data tenaga ahli dari Provider Anda, atau proyek ini belum menunjuk Provider.'}
            </p>
          </div>
        )}

        {!isLoading && !isError && craftmenList.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[460px] overflow-y-auto p-1">
            {craftmenList.map((u: UserSearchItem) => {
              const isSelected = selectedUsers.some((item) => item.id === u.id);
              const isAdded = u.is_already_member;
              const roleInfo = getRoleLabel(u.user_type);

              return (
                <div
                  key={u.id || u.serviceman_id}
                  onClick={() => toggleSelectUser(u)}
                  className={`group relative flex flex-col p-4 rounded-3xl border transition-all duration-200 ${
                    isAdded
                      ? 'border-slate-200 bg-slate-50/50 opacity-60 cursor-not-allowed'
                      : isSelected
                      ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/20 shadow-md cursor-pointer'
                      : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${roleInfo.badge}`}>
                        {roleInfo.label}
                      </span>
                      {isAdded && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                          Sudah Terdaftar
                        </span>
                      )}
                    </div>

                    {!isAdded && (
                      <div className="text-blue-600 transition">
                        {isSelected ? (
                          <CheckCircle2 size={22} className="fill-blue-600 text-white" />
                        ) : (
                          <Circle size={22} className="text-slate-300 group-hover:text-slate-400" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-700 flex items-center justify-center text-white font-extrabold text-sm shadow-sm flex-shrink-0">
                      {u.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className={`font-bold text-sm truncate transition-colors ${isAdded ? 'text-slate-500' : 'text-slate-800 group-hover:text-blue-600'}`}>
                        {u.name}
                      </h4>
                      <p className="text-xs text-slate-400 truncate">{u.email || u.phone || 'Terdaftar'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
